import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

type VirtualPaginationConfig = {
    initialLoad: number;
    loadMore: number;
    loadDebounce: number;
    checkInterval: number;
    rootMargin: string;
};

type UseVirtualPaginationProps<T, TItem = T> = {
    items: T[];
    config?: Partial<VirtualPaginationConfig>;
    /**
     * Optional function to extract nested items from groups.
     * When provided, pagination counts groups based on total nested items,
     * ensuring complete groups are always rendered together.
     *
     * Example: For grouped showings by date where each group contains ticket buttons:
     * - items: array of date groups
     * - getItemsFromGroup: (group) => group.showings
     * - Result: Renders minimum number of date groups to show at least N ticket buttons
     */
    getItemsFromGroup?: (group: T) => TItem[];
};

type UseVirtualPaginationReturn = {
    visibleCount: number;
    loadMoreRef: (node?: Element | null) => void;
    hasMore: boolean;
    reset: () => void;
};

const DEFAULT_CONFIG: VirtualPaginationConfig = {
    initialLoad: 10,
    loadMore: 10,
    loadDebounce: 100,
    checkInterval: 200,
    rootMargin: '200px'
};

/**
 * Custom hook for virtual pagination with intersection observer.
 * Supports both flat lists and grouped/nested structures.
 *
 * **Flat list mode** (default):
 * - Pass items array
 * - Returns visibleCount to slice the array
 *
 * **Grouped mode**:
 * - Pass items array and getItemsFromGroup function
 * - Calculates minimum number of complete groups to show target item count
 * - Never splits groups - always renders complete groups
 *
 * @param items - Array of items to paginate (can be flat or groups)
 * @param getItemsFromGroup - Optional function to extract nested items from groups
 * @param config - Optional configuration to override defaults
 * @returns Object with visibleCount, loadMoreRef, hasMore flag, and reset function
 */
export function useVirtualPagination<T, TItem = T>({
    items,
    getItemsFromGroup,
    config: userConfig
}: UseVirtualPaginationProps<T, TItem>): UseVirtualPaginationReturn {
    const config = { ...DEFAULT_CONFIG, ...userConfig };

    const [targetCount, setTargetCount] = useState<number>(config.initialLoad);
    const loadingRef = useRef(false);

    // For grouped mode: calculate cumulative item counts for each group
    const groupItemCounts = useMemo(() => {
        if (!getItemsFromGroup) return null;
        return items.map((group) => getItemsFromGroup(group).length);
    }, [items, getItemsFromGroup]);

    // Calculate visible count based on mode
    const visibleCount = useMemo(() => {
        if (!getItemsFromGroup || !groupItemCounts) {
            // Flat list mode: return target count directly
            return targetCount;
        }

        // Grouped mode: calculate minimum groups to reach target item count
        let totalItems = 0;
        let groupIndex = 0;

        while (groupIndex < items.length && totalItems < targetCount) {
            totalItems += groupItemCounts[groupIndex];
            groupIndex++;
        }

        return groupIndex;
    }, [items.length, groupItemCounts, targetCount, getItemsFromGroup]);

    // Intersection observer for loading more content
    const { ref: loadMoreRef, inView } = useInView({
        threshold: 0,
        triggerOnce: false,
        rootMargin: config.rootMargin
    });

    // Calculate if there are more items to load
    const hasMore = visibleCount < items.length;

    // Continuous loading function when trigger is in view - optimized with early returns
    const loadMoreIfNeeded = useCallback(() => {
        // Early returns for performance
        if (!inView || loadingRef.current || !hasMore) {
            return;
        }

        loadingRef.current = true;
        const newTargetCount = targetCount + config.loadMore;
        setTargetCount(newTargetCount);

        // Use setTimeout for debouncing
        setTimeout(() => {
            loadingRef.current = false;
        }, config.loadDebounce);
    }, [inView, hasMore, targetCount, config.loadMore, config.loadDebounce]);

    // Load more when trigger comes into view (immediate + continuous)
    useEffect(() => {
        if (!inView) return;

        // Immediate load via microtask to avoid synchronous setState in effect
        const timeoutId = setTimeout(() => loadMoreIfNeeded(), 0);

        const interval = setInterval(() => {
            if (inView && hasMore && !loadingRef.current) {
                loadMoreIfNeeded();
            }
        }, config.checkInterval);

        return () => {
            clearTimeout(timeoutId);
            clearInterval(interval);
        };
    }, [inView, loadMoreIfNeeded, hasMore, config.checkInterval]);

    // Reset target count when items change

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect is intentional; render-time sync infinite-loops with unstable array references
        setTargetCount(config.initialLoad);
        loadingRef.current = false;
    }, [items, config.initialLoad]);

    // Reset function for manual reset
    const reset = useCallback(() => {
        setTargetCount(config.initialLoad);
        loadingRef.current = false;
    }, [config.initialLoad]);

    return {
        visibleCount,
        loadMoreRef,
        hasMore,
        reset
    };
}
